import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { chatTimerService } from "../../services/api";
import messageRequestService from "../../services/messageRequestApi";
import {
  DEFAULT_EXTENSION_CHOICES,
  TIMER_ERROR_COPY,
  hasTimerHint,
  isAgentAgentContext,
  normalizeTimerStatus,
  readEmbeddedStatus,
  remainingForStatus,
  seedFromChannelData,
  stripMessagingPrefix,
} from "../../utils/chatTimer";

// serialize_status also carries `can_deactivate`, `can_reactivate` and, once a
// thread is made permanent, a "deactivated" status (D-011 / T034).
// `normalizeTimerStatus` is a shared util we don't own and it drops keys it
// doesn't know, so we re-attach them here: `canDeactivate` gates the deactivate
// affordance, `canReactivate` (BE PR #176: controller-only, deactivated-only)
// gates the "Reactivate timer" affordance, and `isPermanent` drives the
// ∞ Permanent chip. A permanent thread is neither counting down nor expired.
const enrichStatus = (apiData) => {
  const normalized = normalizeTimerStatus(apiData);
  const raw = apiData || {};
  const isPermanent =
    raw.is_deactivated === true ||
    raw.status === "deactivated" ||
    raw.status === "permanent" ||
    raw.is_permanent === true;
  return {
    ...normalized,
    canDeactivate: raw.can_deactivate === true,
    canReactivate: raw.can_reactivate === true,
    isPermanent,
    isExpired: isPermanent ? false : normalized.isExpired,
  };
};

/*
  Drives the Club<->Agent chat timer for a single Stream channel.

  The backend timer-status endpoint is the source of truth: its can_extend /
  can_revive / is_club fields gate the controls (never a role guess). We fetch it
  on channel open, gated by a cheap `hasTimerHint` so untimed chats don't spam
  404s. While the fetch is in flight we show a display-only chip seeded from
  `channel.data.timer_expires_at`; controls only appear once authoritative data
  arrives. An embedded status object on channel data (zero-fetch / dev harness)
  short-circuits the request.

  Returns a stable shape whether or not the chat carries a timer.
*/
const useChatTimer = (channel) => {
  const channelData = channel?.data || null;
  const channelCid = stripMessagingPrefix(channel?.cid || channel?.id || null);
  const channelKey = channel?.id || channel?.cid || null;

  // phase: idle | loading | loaded | none | error
  const [phase, setPhase] = useState("idle");
  const [status, setStatus] = useState(null);
  const [isMutating, setIsMutating] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());

  // Read channel data through a ref so the effect can re-read the latest seed +
  // hints WITHOUT re-running every time Stream hands us a new channelData object
  // identity. The effect is keyed ONLY on the stable channel identity
  // (channelKey/channelCid) — one authoritative fetch per channel. Keying it on
  // the object identity (the previous behaviour) let a mid-flight re-render's
  // cleanup cancel getStatus before it settled, stranding the timer on its
  // display-only seed (canExtend:false) so the controller's "Extend" button and
  // rights hint never appeared (QA S1 D28/D22 race).
  const channelDataRef = useRef(channelData);
  channelDataRef.current = channelData;

  useEffect(() => {
    const data = channelDataRef.current;
    setStatus(null);
    setPhase("idle");

    // No channel identity → nothing to time.
    if (!channelKey) {
      setPhase("none");
      return undefined;
    }

    // Zero-fetch: a full status object stamped on channel data.
    const embedded = readEmbeddedStatus(data);
    if (embedded) {
      setStatus(enrichStatus(embedded));
      setPhase("loaded");
      return undefined;
    }

    // No hint => not a timer chat; skip the request (and its 404).
    if (!hasTimerHint(data)) {
      setPhase("none");
      return undefined;
    }

    // Show a display-only chip immediately, then confirm with the backend. The
    // authoritative status ALWAYS wins over the seed (it carries can_extend /
    // can_deactivate); only a genuine channel switch (new channelKey) cancels it.
    setStatus(seedFromChannelData(data));
    setPhase("loading");

    let cancelled = false;
    chatTimerService.getStatus(channelCid).then((res) => {
      if (cancelled) return;
      if (res.success) {
        setStatus(enrichStatus(res.data));
        setPhase("loaded");
      } else if (res.notFound) {
        setStatus(null);
        setPhase("none");
      } else {
        // Transient error: keep the display-only seed, no controls.
        setPhase("error");
      }
    });
    return () => {
      cancelled = true;
    };
  }, [channelKey, channelCid]);

  const isTimerChat = phase !== "none" && phase !== "idle" && status !== null;
  const isExpired = Boolean(status?.isExpired);

  // Tick once a minute only while an active timer is on screen (never for a
  // permanent thread — it has no countdown).
  const shouldTick = isTimerChat && !isExpired && !status?.isPermanent;
  useEffect(() => {
    if (!shouldTick) return undefined;
    setNowMs(Date.now());
    const id = setInterval(() => setNowMs(Date.now()), 60 * 1000);
    return () => clearInterval(id);
  }, [shouldTick, status?.expiresAtMs]);

  const remaining = useMemo(() => {
    // A deactivated thread reads as ∞ Permanent, not a countdown or "Expired".
    if (status?.isPermanent) {
      return {
        count: null,
        phrase: "∞ Permanent",
        urgent: false,
        expired: false,
        permanent: true,
      };
    }
    return remainingForStatus(status, nowMs);
  }, [status, nowMs]);

  const applyResult = useCallback((res, successMsg) => {
    if (res.success) {
      setStatus(enrichStatus(res.data));
      setPhase("loaded");
      toast.success(successMsg);
      return true;
    }
    const copy = res.detail && TIMER_ERROR_COPY[res.detail];
    toast.error(copy || res.error || "Chat timer request failed");
    return false;
  }, []);

  const extend = useCallback(
    async (days) => {
      if (!channelCid || isMutating) return { success: false };
      setIsMutating(true);
      try {
        const res = await chatTimerService.extend(channelCid, days);
        applyResult(res, "Conversation extended");
        return res;
      } finally {
        setIsMutating(false);
      }
    },
    [channelCid, isMutating, applyResult]
  );

  const revive = useCallback(
    async (days) => {
      if (!channelCid || isMutating) return { success: false };
      setIsMutating(true);
      try {
        const res = await chatTimerService.revive(channelCid, days);
        applyResult(res, "Conversation revived");
        return res;
      } finally {
        setIsMutating(false);
      }
    },
    [channelCid, isMutating, applyResult]
  );

  // Thread-level deactivation (D-011 (1)): the rights holder makes THIS thread
  // permanent. Controller-only endpoint; the affordance is only shown when the
  // backend reports can_deactivate, so this mirrors the extend/revive gates. On
  // success the chip flips to ∞ Permanent from the refreshed status; if the
  // endpoint returns a thin body we still flip optimistically to permanent.
  const deactivate = useCallback(async () => {
    if (!channelCid || isMutating) return { success: false };
    setIsMutating(true);
    try {
      const res = await messageRequestService.deactivateTimer(channelCid);
      if (res.success) {
        // Thin-body fallback: only the controller can reach a 200 here, so the
        // reactivate capability is true by definition (T034) — without it the
        // menu would lose the "Reactivate timer" entry the modal promises.
        const data =
          res.data && typeof res.data === "object" && Object.keys(res.data).length
            ? res.data
            : { status: "deactivated", can_reactivate: true };
        setStatus(enrichStatus(data));
        setPhase("loaded");
        toast.success("Timer deactivated — this thread won't expire");
      } else {
        const copy = res.detail && TIMER_ERROR_COPY[res.detail];
        toast.error(copy || res.error || "Could not deactivate the timer");
      }
      return res;
    } finally {
      setIsMutating(false);
    }
  }, [channelCid, isMutating]);

  // The inverse of `deactivate` (T034 / BE PR #176): the rights holder turns a
  // ∞ Permanent thread's countdown back on. Controller-only endpoint; the
  // affordance is only shown when the backend reports can_reactivate, so this
  // mirrors the extend/revive/deactivate gates. The response is a full
  // serialize_status, so `applyResult` flips the chip back to a countdown.
  const reactivate = useCallback(
    async (days) => {
      if (!channelCid || isMutating) return { success: false };
      setIsMutating(true);
      try {
        const res = await chatTimerService.reactivate(channelCid, days);
        applyResult(res, "Timer reactivated — this thread expires again");
        return res;
      } finally {
        setIsMutating(false);
      }
    },
    [channelCid, isMutating, applyResult]
  );

  return {
    isTimerChat,
    isExpired,
    isClubSide: Boolean(status?.isClub),
    // The timer context (M4-F3): consumers branch labels on this, NOT on is_club
    // (true for the agent_agent accepter). agent_agent → neutral wording.
    context: status?.context || null,
    isAgentAgent: isAgentAgentContext(status?.context),
    canExtend: Boolean(status?.canExtend),
    canRevive: Boolean(status?.canRevive),
    canDeactivate: Boolean(status?.canDeactivate),
    canReactivate: Boolean(status?.canReactivate),
    isPermanent: Boolean(status?.isPermanent),
    remaining,
    expiresAtMs: status?.expiresAtMs ?? null,
    extensionChoices: status?.extensionChoices ?? DEFAULT_EXTENSION_CHOICES,
    isMutating,
    extend,
    revive,
    deactivate,
    reactivate,
  };
};

export default useChatTimer;
